import { type QueryClient } from '@tanstack/react-query';

import { type Clip } from '@air/api/types';
import { type SelectableItem } from '@air/redux-selected-items';

import {
  fetchPrivateWorkspaceAgentClipsListByIds,
  getPrivateWorkspaceAgentClipsListByIdsKey,
} from '~/components/v2/components/workspace/workspace-agent-panel/queries/usePrivateWorkspaceAgentClipsListByIds';
import { centralizedClipSelector } from '~/store/centralizedClip/selectors';
import { selectedClipItemsSelector } from '~/store/selectedItems/selectors';
import { type AirState } from '~/store/types';
import { getAssetGalleryItemType } from '~/utils/AssetUtils';

type ListByIdsClipRow = Awaited<ReturnType<typeof fetchPrivateWorkspaceAgentClipsListByIds>>[number];

export const stableListByIdsClipIds = (clipIds: readonly Clip['id'][]): Clip['id'][] => [...new Set(clipIds)].sort();

export type ResolveWorkspaceAgentClipIdsScopeAsyncParams = {
  clipIds: Clip['id'][];
  queryClient: QueryClient;
  state: AirState;
  workspaceId: string | undefined;
};

/** Sync: workspace selection + centralized clip only (safe for Redux selectors). */
export const resolveSelectableItemsForClipIds = (state: AirState, clipIds: Clip['id'][]): SelectableItem[] => {
  const selectedById = new Map(selectedClipItemsSelector(state).map((item) => [item.id, item]));
  const centralizedClip = centralizedClipSelector(state);
  const resolved: SelectableItem[] = [];

  for (const clipId of clipIds) {
    const fromSelection = selectedById.get(clipId);

    if (fromSelection) {
      resolved.push(fromSelection);
      continue;
    }

    if (centralizedClip?.id === clipId) {
      resolved.push({
        id: clipId,
        item: centralizedClip,
        type: getAssetGalleryItemType(centralizedClip),
      });
    }
  }

  return resolved;
};

const selectableItemsFromClips = (
  clipIds: Clip['id'][],
  clips?: ReadonlyArray<ListByIdsClipRow | Clip>,
): SelectableItem[] | undefined => {
  if (!clips?.length || clipIds.length === 0) {
    return undefined;
  }

  const clipIdSet = new Set(clipIds);

  const items = clips
    .filter((clip) => clipIdSet.has(clip.id))
    .map((clip) => ({
      id: clip.id,
      item: clip,
      type: getAssetGalleryItemType(clip),
    }));

  return items.length > 0 ? items : undefined;
};

const mergeClipsIntoResolved = (
  clipIds: Clip['id'][],
  resolvedById: Map<string, SelectableItem>,
  clips: ReadonlyArray<ListByIdsClipRow | Clip>,
): void => {
  const unresolvedIds = clipIds.filter((clipId) => !resolvedById.has(clipId));
  const selectableItems = selectableItemsFromClips(unresolvedIds, clips);

  for (const item of selectableItems ?? []) {
    resolvedById.set(item.id, item);
  }
};

const fetchListByIdsClips = (
  queryClient: QueryClient,
  workspaceId: string,
  clipIds: Clip['id'][],
): Promise<ListByIdsClipRow[]> => {
  const listByIdsParams = { clipIds: stableListByIdsClipIds(clipIds), workspaceId };

  return queryClient.fetchQuery({
    queryKey: getPrivateWorkspaceAgentClipsListByIdsKey(listByIdsParams),
    queryFn: () => fetchPrivateWorkspaceAgentClipsListByIds(listByIdsParams),
  });
};

/** Redux, then `Clips.listByIds` when ids are still missing. */
export const resolveSelectableItemsForClipIdsAsync = async ({
  clipIds,
  queryClient,
  state,
  workspaceId,
}: ResolveWorkspaceAgentClipIdsScopeAsyncParams): Promise<SelectableItem[]> => {
  const resolvedById = new Map(
    resolveSelectableItemsForClipIds(state, clipIds).map((item) => [item.id, item] as const),
  );

  const unresolvedIds = () => clipIds.filter((clipId) => !resolvedById.has(clipId));

  if (unresolvedIds().length > 0 && workspaceId) {
    mergeClipsIntoResolved(clipIds, resolvedById, await fetchListByIdsClips(queryClient, workspaceId, unresolvedIds()));
  }

  return clipIds.flatMap((clipId) => {
    const item = resolvedById.get(clipId);

    return item ? [item] : [];
  });
};
